import { alpha } from "@mui/material";
import { COLOR_BLACK, COLOR_TEXT_MUTE, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK, FONT_FAMILY_KNOCKOUT } from "helpers/FONT";
import { SPACING, SPACING_DENSE, SPACING_HALF } from "helpers/spacings";
import React from "react";

function build3PTSegment(segments) {
  const pointsSeg = segments.find((s) => s.title === "Points");
  if (!pointsSeg) return null;

  const threePtIdx = pointsSeg.columns.findIndex((c) => c.shortName === "3PM-A");
  const gamesIdx = pointsSeg.columns.findIndex((c) => c.shortName === "Games");
  if (threePtIdx === -1 || gamesIdx === -1) return null;

  const scored = pointsSeg.rows.map((row) => {
    const made = row.cells[threePtIdx]?.made ?? 0;
    const games = parseFloat(row.cells[gamesIdx]?.value) || 1;
    const perGame = Math.round((made / games) * 10) / 10;
    return { row, made, perGame };
  });

  scored.sort((a, b) => b.perGame - a.perGame || b.made - a.made);

  let rank = 1;
  const rows = scored.map(({ row, perGame, made }, i) => {
    if (i > 0 && scored[i - 1].perGame !== perGame) rank = i + 1;
    return {
      ...row,
      rank,
      cells: [
        { statType: "simple", value: String(perGame) },
        { statType: "simple", value: String(made) },
      ],
    };
  });

  return {
    title: "3PT",
    columns: [
      { shortName: "3PM", name: "3PT made per game" },
      { shortName: "Total", name: "Total 3PT made" },
    ],
    rows,
  };
}

// Display order for the leader cards (most commonly-tracked categories first). Segments not
// listed here (e.g. Turnovers) fall to the end in their original order.
const CARD_ORDER = ["Points", "3PT", "Assists", "Rebounds", "Steals", "Blocks"];

export default function LeaderboardOverview({ segments, statMode = "perGame" }) {
  const threePointSegment = build3PTSegment(segments);
  const allSegments = threePointSegment ? [...segments, threePointSegment] : segments;

  const orderIndex = (title) => {
    const i = CARD_ORDER.indexOf(title);
    return i === -1 ? CARD_ORDER.length : i;
  };
  const orderedSegments = [...allSegments].sort((a, b) => orderIndex(a.title) - orderIndex(b.title));

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: SPACING,
      }}
    >
      {orderedSegments.map((segment) => (
        <LeaderboardOverviewCard key={segment.title} segment={segment} statMode={statMode} />
      ))}
    </div>
  );
}

function LeaderboardOverviewCard({ segment, statMode }) {
  const total = statMode === "total";
  const cols = segment.columns || [];
  const totalIdx = cols.findIndex((c) => c.shortName === "Total");
  const highIdx = cols.findIndex((c) => c.shortName === "High");
  // In Total mode show the season total when the segment provides one; otherwise stay per-game.
  const showTotal = total && totalIdx !== -1;
  const valueIdx = showTotal ? totalIdx : 0;

  // Rank by whichever value is currently shown (per-game average or season total).
  const ranked = segment.rows
    .map((row) => ({ row, value: parseFloat(row.cells[valueIdx]?.value) || 0 }))
    .sort((a, b) => b.value - a.value);
  let rank = 1;
  const rows = ranked.slice(0, 5).map((entry, i) => {
    if (i > 0 && ranked[i - 1].value !== entry.value) rank = i + 1;
    return { ...entry, rank };
  });

  return (
    <div
      style={{
        backgroundColor: COLOR_WHITE_LIGHTER,
        borderRadius: 12,
        border: `1px solid ${alpha(COLOR_BLACK, 0.1)}`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: `${SPACING_HALF} ${SPACING}`,
          fontFamily: FONT_FAMILY_KNOCKOUT,
          fontWeight: 400,
          fontSize: 15,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: COLOR_BLACK,
          backgroundColor: "#FAFAFA",
          borderBottom: `1px solid ${alpha(COLOR_BLACK, 0.08)}`,
        }}
      >
        {segment.title} {showTotal ? "TOTAL" : "PER GAME"}
      </div>

      {rows.map(({ row, rank }, index) => {
        const primaryValue = row.cells[valueIdx]?.value;
        const highValue = highIdx !== -1 ? row.cells[highIdx]?.value : null;

        return (
          <div
            key={index}
            style={{
              display: "flex",
              flexFlow: "row nowrap",
              alignItems: "center",
              padding: `${SPACING_HALF} ${SPACING}`,
              gap: SPACING_HALF,
              borderBottom: index < rows.length - 1 ? `1px solid ${alpha(COLOR_BLACK, 0.06)}` : "none",
              backgroundColor: row.highlighted ? COLOR_WHITE_LIGHTER : undefined,
            }}
          >
            <div
              style={{
                width: 20,
                flexShrink: 0,
                fontFamily: FONT_FAMILY_GRAPHIK,
                fontSize: 12,
                fontWeight: 600,
                color: COLOR_TEXT_MUTE,
              }}
            >
              {rank}.
            </div>

            <div style={{ flex: 1, minWidth: 0, display: "flex", flexFlow: "column nowrap", gap: SPACING_DENSE }}>
              <div
                style={{
                  fontFamily: FONT_FAMILY_GRAPHIK,
                  fontWeight: 500,
                  fontSize: 13,
                  color: COLOR_BLACK,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.headCells[0]}
              </div>
              {row.headCells[1] && (
                <div
                  style={{
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    fontSize: 12,
                    color: COLOR_TEXT_MUTE,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.headCells[1]}
                </div>
              )}
            </div>

            <div
              style={{
                textAlign: "right",
                flexShrink: 0,
                display: "flex",
                flexFlow: "column nowrap",
                gap: SPACING_DENSE,
              }}
            >
              <div
                style={{
                  fontFamily: FONT_FAMILY_GRAPHIK,
                  fontWeight: 500,
                  fontSize: 15,
                  letterSpacing: 2,
                  color: COLOR_BLACK,
                }}
              >
                {primaryValue ?? "—"}
              </div>
              {highValue && highValue !== "0" && (
                <div
                  style={{
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    fontSize: 12,
                    color: COLOR_TEXT_MUTE,
                  }}
                >
                  {highValue} high
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
